import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import { List } from "antd"
import { Editor } from "@tiptap/react"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import { notify } from "shared/ui/Notifications/notify"
import handleSequenceError from "utils/handleSequenceError"
import { escapeHTML } from "utils/escapeHtml"
import QueryListItem from "./QueryListItem.component"
import { Project } from "stores/projectStore"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"
import { useQueryBuilderStore } from "stores/queryBuilderStore"
import { SavedQueryEnvelope } from "types/savedQuery.types"

// Define the query item structure
interface QueryItem {
  key: string
  title: string
  isCreateNew: boolean
}

// Define the props interface for the SavedQueries component
interface SavedQueriesProps {
  /** Project information containing id and creator */
  project: Project
  /** Function to toggle between saved queries view and editor */
  toggleSaved: () => void
  /** TipTap editor instance */
  editor: Editor | null
  /** Array of saved query names */
  projectQueries: string[]
  /** Function to refresh the queries list */
  refreshQueries: () => void
  /** Currently selected query name */
  selectedQuery: string
  /** Function to update selected query */
  setSelectedQuery: (query: string) => void
  /** Function to update content changed state */
  setContentChanged: (changed: boolean) => void
  /** Saved queries on the Run on load list. */
  onLoadNames?: string[]
}

/**
 * SavedQueries: bucketed list of every saved record in the project, grouped by
 * kind (Pipelines / Builders / Scripts / Needs attention). Each kind routes to
 * its native editor on click — pipelines open in the Transform tab, builders
 * in the Builder tab, scripts continue to load into the Console editor.
 *
 * Bucketing requires fetching each query's metadata sidecar; we lazy-load on
 * first mount per project and cache via component state. Failures fall back
 * to a flat "Scripts" listing so the user is never blocked by a misbehaving
 * sidecar.
 */
const SavedQueries: React.FC<SavedQueriesProps> = ({
  project,
  toggleSaved,
  editor,
  projectQueries,
  refreshQueries,
  selectedQuery,
  setSelectedQuery,
  setContentChanged,
  onLoadNames = [],
}) => {
  const { getQuery, deleteQuery, listStructured } = useSavedQueries()
  const setViewMode = useQueryConsoleLayoutStore((s) => s.setViewMode)

  // Each entry is the envelope as parsed from body+sidecar. `null` means
  // the bucketing fetch failed entirely; we treat those as scripts.
  const [envelopeMap, setEnvelopeMap] = useState<
    Map<string, SavedQueryEnvelope>
  >(new Map())
  const [bucketsLoading, setBucketsLoading] = useState(false)

  // Refresh envelopes whenever the project's query list changes.
  useEffect(() => {
    let cancelled = false
    if (!project.id || !project.Created_by) return
    setBucketsLoading(true)
    listStructured(project.id, project.Created_by)
      .then((envelopes) => {
        if (cancelled) return
        const next = new Map<string, SavedQueryEnvelope>()
        envelopes.forEach((env) => next.set(env.name, env))
        setEnvelopeMap(next)
      })
      .catch((err) => {
        console.warn("SavedQueries: failed to load envelopes:", err)
      })
      .finally(() => {
        if (!cancelled) setBucketsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [project.id, project.Created_by, projectQueries, listStructured])

  const handleSelectScript = useCallback(
    async (_e: Event | null, item: string): Promise<void> => {
      if (!editor) return
      try {
        const queryRes = await getQuery(project.id, project.Created_by, item)
        const content = queryRes.data
          .split("\n")
          .map((line: string) => `<p>${escapeHTML(line)}</p>`)
          .join("")
        React.startTransition(() => {
          editor.commands.clearContent()
          editor.commands.insertContent(content)
          setSelectedQuery(item)
          setContentChanged(false)
          toggleSaved()
        })
      } catch (error) {
        console.error("Error loading script:", error)
        throw error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      project.id,
      project.Created_by,
      editor,
      setSelectedQuery,
      setContentChanged,
      toggleSaved,
    ]
  )

  /** Open a saved pipeline in the Transform tab. The pipeline itself was
   *  already hydrated into the store on project mount; here we just route. */
  const handleSelectPipeline = useCallback(
    (_e: Event | null, name: string): void => {
      const state = useQueryBuilderStore.getState()
      const pipeline = state.transformPipelines.find((p) => p.name === name)
      if (pipeline) {
        state.setActiveTransformPipeline(pipeline.id)
      }
      setSelectedQuery(name)
      setViewMode("transform")
    },
    [setSelectedQuery, setViewMode]
  )

  /** Open a saved builder in the Builder tab — same idea as pipelines. */
  const handleSelectBuilder = useCallback(
    (_e: Event | null, name: string): void => {
      const state = useQueryBuilderStore.getState()
      const builder = state.builders.find((b) => b.name === name)
      if (builder) {
        state.setActiveBuilder(builder.id)
      }
      setSelectedQuery(name)
      setViewMode("builder")
    },
    [setSelectedQuery, setViewMode]
  )

  const deleteSavedQuery = useCallback(
    async (e: React.MouseEvent, item: string): Promise<void> => {
      try {
        e.stopPropagation()
        const deleteRes = await deleteQuery(
          project.id,
          project.Created_by,
          item
        )
        if (handleSequenceError(deleteRes.data, "Error deleting query")) {
          React.startTransition(() => {
            setSelectedQuery("")
            refreshQueries()
            notify.success(
              "Query Deleted successfully!",
              "Query has been permanently deleted."
            )
          })
        }
      } catch (error) {
        console.error(`Error in API call:`, error)
        throw error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project.id, project.Created_by, deleteQuery, refreshQueries]
  )

  const handleCreateNew = useCallback((): void => {
    if (!editor) return
    React.startTransition(() => {
      editor.commands.clearContent()
      setSelectedQuery("")
      toggleSaved()
    })
  }, [editor, setSelectedQuery, toggleSaved])

  /** Bucket the project's saved query names by parsed kind. Names that
   *  haven't been fetched yet (or failed to fetch) land in `scripts` so the
   *  user always sees them — the component degrades gracefully when sidecars
   *  are missing. */
  const buckets = useMemo(() => {
    const pipelines: QueryItem[] = []
    const builders: QueryItem[] = []
    const scripts: QueryItem[] = []
    const corrupt: QueryItem[] = []
    for (const name of projectQueries) {
      if (!name.trim()) continue
      const env = envelopeMap.get(name)
      const item: QueryItem = { key: name, title: name, isCreateNew: false }
      if (!env) {
        // Pre-fetch state — show as a script (the safest default).
        scripts.push(item)
        continue
      }
      switch (env.kind) {
        case "pipeline":
          pipelines.push(item)
          break
        case "builder":
          builders.push(item)
          break
        case "script":
          scripts.push(item)
          break
        case "tabspec":
        case "runonload":
          // Tab specs have their own tab and the Run on load list its own panel; never listed here.
          break
        default:
          corrupt.push(item)
      }
    }
    return { pipelines, builders, scripts, corrupt }
  }, [projectQueries, envelopeMap])

  const renderSection = (
    title: string,
    helperText: string,
    items: QueryItem[],
    onSelect: (e: Event | null, item: string) => void | Promise<void>
  ): React.ReactNode => {
    if (items.length === 0) return null
    return (
      <div className="qc-saved__section">
        <div className="qc-saved__section-header">
          <span className="qc-saved__section-title">{title}</span>
          <span className="qc-saved__section-helper">{helperText}</span>
        </div>
        <List
          itemLayout="horizontal"
          dataSource={items}
          renderItem={(item: QueryItem) => (
            <QueryListItem
              key={item.key}
              item={item}
              selectedQuery={selectedQuery}
              onDelete={deleteSavedQuery}
              onSelect={onSelect}
              onCreate={handleCreateNew}
              badge={onLoadNames.includes(item.title) ? "on load" : undefined}
            />
          )}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full qc-saved">
      <div className="flex-grow overflow-y-auto">
        <div className="qc-saved__section">
          <List
            itemLayout="horizontal"
            dataSource={[
              { key: "new", title: "Create New Query", isCreateNew: true },
            ]}
            renderItem={(item: QueryItem) => (
              <QueryListItem
                key={item.key}
                item={item}
                selectedQuery={selectedQuery}
                onDelete={deleteSavedQuery}
                onSelect={handleSelectScript}
                onCreate={handleCreateNew}
                badge={onLoadNames.includes(item.title) ? "on load" : undefined}
              />
            )}
          />
        </div>
        {renderSection(
          "Pipelines",
          "Open in Transform",
          buckets.pipelines,
          handleSelectPipeline
        )}
        {renderSection(
          "Builders",
          "Open in Builder",
          buckets.builders,
          handleSelectBuilder
        )}
        {renderSection(
          "Scripts",
          "Open in Console",
          buckets.scripts,
          handleSelectScript
        )}
        {renderSection(
          "Needs attention",
          "Sidecar metadata couldn't be parsed",
          buckets.corrupt,
          handleSelectScript
        )}
        {bucketsLoading && projectQueries.length > 0 && envelopeMap.size === 0 && (
          <div className="qc-saved__loading">Loading saved queries…</div>
        )}
      </div>
    </div>
  )
}

export default memo(SavedQueries)
